import { Grid, Group, Radio, Select, Textarea, TextInput, Title } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { ProfileCheckboxSection } from '../../../components/ProfileCheckboxSection';
import type { ProviderSettingsProfileTabFormValues } from '@/schemas/practitioner.schema';
import { digitsOnly } from '../../../lib/formValidation';
import {
  AGE_GROUP_OPTIONS,
  CLINICAL_SPECIALTIES_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  LANGUAGES_OPTIONS,
  LGBTQIA_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  RELIGION_OPTIONS,
  TREATMENT_MODALITIES_OPTIONS,
  US_STATES,
} from './userSettingsProfileConstants';

function formatPhoneNumber(value: string): string {
  const d = digitsOnly(value).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ProviderProfileSettingsFields({
  form,
}: {
  form: UseFormReturnType<ProviderSettingsProfileTabFormValues>;
}): JSX.Element {
  return (
    <Grid>
      <Grid.Col span={12}>
        <Group grow align="flex-start">
          <TextInput
            label="Preferred first name"
            placeholder="Preferred first name"
            {...form.getInputProps('preferredFirstName')}
          />
          <TextInput
            label="Preferred last name"
            placeholder="Preferred last name"
            {...form.getInputProps('preferredLastName')}
          />
        </Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          label="Pronouns"
          placeholder="e.g. she/her"
          {...form.getInputProps('pronouns')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          label="Phone Number"
          placeholder="(555) 555-5555"
          withAsterisk
          {...form.getInputProps('phone')}
          onChange={(event) => {
            form.setFieldValue('phone', formatPhoneNumber(event.currentTarget.value));
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          label="Mailing Address"
          placeholder="Street address"
          withAsterisk
          {...form.getInputProps('street')}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <TextInput label="City" placeholder="City" withAsterisk {...form.getInputProps('city')} />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Select
          label="State"
          placeholder="State"
          data={US_STATES}
          withAsterisk
          {...form.getInputProps('state')}
          value={form.values.state || null}
          onChange={(value) => form.setFieldValue('state', value ?? '')}
          clearable
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <TextInput
          label="Zip Code"
          placeholder="Zip code"
          withAsterisk
          {...form.getInputProps('zipCode')}
          id="provider-profile-postal-code"
          name="provider-profile-postal-code"
          autoComplete="section-provider-profile postal-code"
          inputMode="numeric"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
          readOnly
          onFocus={(e) => {
            e.currentTarget.removeAttribute('readOnly');
          }}
          onChange={(event) => {
            form.setFieldValue('zipCode', digitsOnly(event.currentTarget.value).slice(0, 5));
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Profile Bio"
          placeholder="Tell clients a little about yourself"
          rows={7}
          withAsterisk
          {...form.getInputProps('bio')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Languages Spoken"
          options={LANGUAGES_OPTIONS}
          columns={3}
          value={form.values.languages}
          onChange={(value) => form.setFieldValue('languages', value)}
          withAsterisk
          error={typeof form.errors.languages === 'string' ? form.errors.languages : undefined}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Clinical Specialties"
          options={CLINICAL_SPECIALTIES_OPTIONS}
          columns={3}
          value={form.values.specialties}
          onChange={(value) => form.setFieldValue('specialties', value)}
          withAsterisk
          error={typeof form.errors.specialties === 'string' ? form.errors.specialties : undefined}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Treatment Modalities"
          options={TREATMENT_MODALITIES_OPTIONS}
          columns={2}
          value={form.values.modalities}
          onChange={(value) => form.setFieldValue('modalities', value)}
          withAsterisk
          error={typeof form.errors.modalities === 'string' ? form.errors.modalities : undefined}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Preferred Patient Age Groups"
          options={AGE_GROUP_OPTIONS}
          columns={2}
          value={form.values.ageGroups}
          onChange={(value) => form.setFieldValue('ageGroups', value)}
          withAsterisk
          error={typeof form.errors.ageGroups === 'string' ? form.errors.ageGroups : undefined}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <div>
          <Title order={5} mb="xs" c="dark">
            Gender Identity
          </Title>
          <Radio.Group
            value={form.values.genderIdentity}
            onChange={(value) => form.setFieldValue('genderIdentity', value)}
          >
            <Group gap="lg">
              {GENDER_IDENTITY_OPTIONS.map((option) => (
                <Radio key={option} value={option} label={option} />
              ))}
            </Group>
          </Radio.Group>
        </div>
      </Grid.Col>

      <Grid.Col span={12}>
        <div>
          <Title order={5} mb="xs" c="dark">
            Sexual Identity / LGBTQIA+
          </Title>
          <Radio.Group
            value={form.values.lgbtqiaIdentity}
            onChange={(value) => form.setFieldValue('lgbtqiaIdentity', value)}
          >
            <Group gap="lg">
              {LGBTQIA_OPTIONS.map((option) => (
                <Radio key={option} value={option} label={option} />
              ))}
            </Group>
          </Radio.Group>
        </div>
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Religion and Spirituality"
          options={RELIGION_OPTIONS}
          columns={3}
          value={form.values.religion}
          onChange={(value) => form.setFieldValue('religion', value)}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Race and Ethnicity"
          options={RACE_ETHNICITY_OPTIONS}
          columns={3}
          value={form.values.raceEthnicity}
          onChange={(value) => form.setFieldValue('raceEthnicity', value)}
        />
      </Grid.Col>
    </Grid>
  );
}
